import PropTypes from "prop-types";
import React from "react";
import { Helmet } from "react-helmet-async";

import { StructuredData } from "./StructuredData";

export const SEOHead = ({
  puppy,
  pageType = "puppy",
  title,
  description,
  image,
  url,
}) => {
  // Generate dynamic content based on page type and puppy data
  const generateTitle = () => {
    if (title) return title;

    switch (pageType) {
      case "puppy":
        return puppy
          ? `${puppy.cardHeaderTitle} - Trained ${puppy.breed} Puppy | Todo's Translator`
          : "Trained Puppies for Sale | Todo's Translator";
      case "puppies-list":
        return "Trained Puppies for Sale | Professional Dog Training | Todo's Translator";
      case "home":
        return "Todo's Translator | Trained Puppies Delivered Nationwide | Professional Dog Training";
      case "contact":
        return "Contact Us | Trained Puppies Delivered Nationwide | Todo's Translator";
      case "about":
        return "About Todo's Translator | Professional Dog Training | Nationwide Delivery";
      default:
        return "Todo's Translator | Professional Dog Training & Trained Puppies";
    }
  };

  const generateDescription = () => {
    if (description) return description;

    switch (pageType) {
      case "puppy":
        return puppy
          ? `Meet ${puppy.cardHeaderTitle}, ${puppy.description ? puppy.description.substring(0, 150) : `a trained ${puppy.breed} puppy ready for adoption`}. Professional training included.`
          : "Professional trained puppies for sale. Each puppy comes fully house-trained, crate-trained, and socialized.";
      case "puppies-list":
        return "Browse our selection of professionally trained puppies for sale. Each puppy is house-trained, crate-trained, and socialized. Nationwide delivery available to San Francisco, Seattle, NYC, Boston, Chicago, Denver, and all 50 states.";
      case "home":
        return "Todo's Translator offers professionally trained puppies delivered nationwide. Expert training, lifetime support, and exceptional results. Serving San Francisco, Seattle, NYC, Boston, Chicago, Denver, Nashville, Austin, and more.";
      case "contact":
        return "Contact Todo's Translator for professionally trained puppy adoption with nationwide delivery. Serving major cities coast to coast including San Francisco, Seattle, NYC, Boston, Chicago, Denver, and more. Call (317) 954-6952 or message us today.";
      case "about":
        return "Learn about Todo's Translator's professional dog training methods and trained puppy program. Expert training with proven results. Nationwide delivery available.";
      default:
        return "Todo's Translator - Professional dog training and trained puppies delivered nationwide. Serving Chicago, Denver, Nashville, Indianapolis, and more.";
    }
  };

  const generateImage = () => {
    if (image) return image;
    if (
      puppy &&
      puppy.carouselOptimized &&
      puppy.carouselOptimized.length > 0
    ) {
      return puppy.carouselOptimized[0];
    }
    if (puppy && puppy.images && puppy.images.length > 0)
      return puppy.images[0];
    // Fallback to default site image
    return "/pawprint.svg";
  };

  const generateUrl = () => {
    if (url) return url;
    if (typeof window !== "undefined") {
      return window.location.href;
    }
    return "https://www.buyatrainedpuppy.com"; // Update with your actual domain
  };

  return (
    <>
      <Helmet>
        {/* Basic Meta Tags */}
        <title>{generateTitle()}</title>
        <meta name="description" content={generateDescription()} />
        <meta
          name="keywords"
          content="trained puppies, buy trained puppy, professionally trained dogs, puppy delivery, San Francisco trained puppies, Seattle trained puppies, NYC trained puppies, Boston trained puppies, Chicago trained puppies, Denver trained puppies, Nashville trained puppies, Austin trained puppies, Minneapolis trained puppies, Charlotte trained puppies, Columbus trained puppies, Indianapolis dog training, nationwide puppy delivery, house trained puppies, crate trained puppies"
        />

        {/* Open Graph Tags for Social Sharing */}
        <meta property="og:title" content={generateTitle()} />
        <meta property="og:description" content={generateDescription()} />
        <meta property="og:image" content={generateImage()} />
        <meta property="og:url" content={generateUrl()} />
        <meta
          property="og:type"
          content={pageType === "puppy" ? "product" : "website"}
        />
        <meta property="og:site_name" content="Todo's Translator" />

        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={generateTitle()} />
        <meta name="twitter:description" content={generateDescription()} />
        <meta name="twitter:image" content={generateImage()} />

        {/* Additional SEO Tags */}
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={generateUrl()} />

        {/* Service Area Information - Nationwide Delivery */}
        {pageType === "home" && (
          <>
            <meta name="geo.region" content="US" />
            <meta name="geo.placename" content="Nationwide" />
            <meta
              name="coverage"
              content="San Francisco CA, Seattle WA, New York NY, Boston MA, Chicago IL, Denver CO, Minneapolis MN, Nashville TN, Austin TX, Charlotte NC, Columbus OH, Greenville SC, Champaign IL, Indianapolis IN"
            />
          </>
        )}
      </Helmet>

      {/* Structured Data for Search Engines */}
      {pageType === "home" && (
        <>
          <StructuredData type="organization" />
          <StructuredData type="localbusiness" />
          <StructuredData type="website" />
          <StructuredData type="service" />
        </>
      )}

      {pageType === "puppy" && puppy && (
        <StructuredData type="puppy" data={puppy} />
      )}

      {pageType === "puppies-list" && (
        <>
          <StructuredData type="organization" />
          <StructuredData type="service" />
        </>
      )}

      {pageType === "contact" && (
        <>
          <StructuredData type="organization" />
          <StructuredData type="localbusiness" />
        </>
      )}
    </>
  );
};

SEOHead.propTypes = {
  puppy: PropTypes.object,
  pageType: PropTypes.oneOf([
    "puppy",
    "puppies-list",
    "home",
    "contact",
    "about",
  ]),
  title: PropTypes.string,
  description: PropTypes.string,
  image: PropTypes.string,
  url: PropTypes.string,
};
